import * as angular from "angular";
import { DialogUtils } from "../../helper/dialog.helper";
import { DashboardDataProvider } from "../../data-provider/dashboard.dp";
import { Dashboard } from "../../models/dashboard.model";
import { GridItemHTMLElement, GridStack } from "gridstack";
import { DashboardWidget } from "../../models/dashboard-widget.model";
import { GridStackOptions } from "gridstack/dist/types";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { Campaign } from "../../models/campaign.model";
import { DashboardWidgetFilter } from "../../models/dashboard-widget-filter.model";
import { PermissionCheck } from "../../shared/permission";
import { UserDataProvider } from "../../data-provider/user.dp";
import { User } from "../../models/user.model";

let DashboardComponent = {
    selector: "vcDashboard",
    templateUrl: "/angular/components/dashboard/dashboard.component.html",
    bindings: {
        dashboard: "=",
        filter: "=?",
        downloading: "=?",
    },
    controller: class DashboardController {
        // * bindings for typescript compiler *
        public dashboard?: Dashboard;
        public filter?: DashboardWidgetFilter;
        public downloading: boolean;
        // ************************************

        private readonly $scope: any;
        private readonly $timeout: any;
        private readonly $element: any;
        private localStorage: any;
        private dialog: DialogUtils;
        private dataProvider: DashboardDataProvider;
        public loading: boolean = true;
        public reportsLoading: boolean = true;
        public initialLoadComplete: boolean = false;
        public options: GridStackOptions;
        public grid: GridStack;
        public activeCampaign: Campaign;
        public permissionProvider: PermissionCheck;
        public user: User;

        constructor(
            $scope: any,
            $timeout: any,
            $element: any,
            DialogUtils: DialogUtils,
            CampaignDataProvider: CampaignDataProvider,
            DashboardDataProvider: DashboardDataProvider,
            UserDataProvider: UserDataProvider,
            PermissionCheck: PermissionCheck
        ) {
            this.$scope = $scope;
            this.$timeout = $timeout;
            this.$element = $element;
            this.dialog = DialogUtils;
            this.dataProvider = DashboardDataProvider;
            this.activeCampaign = CampaignDataProvider.getActiveCampaign();
            this.localStorage = this.activeCampaign.getLocalStorage();
            this.permissionProvider = PermissionCheck;
            this.user = UserDataProvider.getActiveUser();

            this.options = {
                column: 6,
                cellHeight: 100,
                margin: 6,
                removable: false,
                draggable: {
                    handle: ".drag-handle",
                },
                disableResize: false,
                disableDrag: false,
                resizable: { autoHide: true },
            };

            this.$scope.$watch(
                () => this.dashboard.id,
                (
                    newValue: string | number,
                    oldValue: string | number
                ): void => {
                    if (newValue && newValue !== oldValue) {
                        this.dashboardChanged();
                    }
                },
                true
            );

            $scope.$on("widgetCreated", (event, widget) => {
                this.widgetCreated(widget);
            });

            $scope.$on("widgetDeleted", (event, widget) => {
                this.widgetDeleted(widget);
            });
        }

        $onInit() {
            this.permissionProvider.getFeaturePermissions().then((features) => {
                this.dashboardChanged();
            });
        }

        dashboardChanged() {
            this.loading = true;
            this.reportsLoading = true;

            if (this.dashboard && this.dashboard.id) {
                this.dashboard.hasEditPermission =
                    this.permissionProvider.hasPermission("EDIT_DASHBOARD") ||
                    parseInt(this.user.uid) == this.dashboard.creator;

                this.options.disableResize = !this.dashboard.hasEditPermission;
                this.options.disableDrag = !this.dashboard.hasEditPermission;

                if (this.grid) this.grid.destroy(false);
                this.grid = null;

                if (this.dashboard.location == "user") {
                    if (!this.localStorage.userPage)
                        this.localStorage.userPage = {};

                    this.localStorage.userPage.selectedDashboardId =
                        this.dashboard.id;
                    this.activeCampaign.localInfo.userPage =
                        this.localStorage.userPage;
                } else {
                    if (!this.localStorage.resultPage)
                        this.localStorage.resultPage = {};

                    this.localStorage.resultPage.selectedDashboardId =
                        this.dashboard.id;
                    this.activeCampaign.localInfo.resultPage =
                        this.localStorage.resultPage;
                }

                this.activeCampaign.saveLocalStorage();

                this.initialLoadComplete = true;
                this.$timeout(() => {
                    this.initializeGrid();
                }, 100);
            }
        }

        initializeGrid() {
            this.grid = GridStack.init(this.options);
            this.grid.on("resizestop", (event, items) => {
                this.saveDashboard();
                this.broadcastResize();
            });
            this.grid.on("dragstop", (event, items) => {
                this.saveDashboard();
            });
            this.grid.on("removed", (event, items) => {
                this.saveDashboard();
            });
            this.grid.on("added", (event, items) => {
                this.saveDashboard();
            });
            this.loading = false;

            this.$timeout(() => {
                this.reportsLoading = false;
            }, 100);
        }

        widgetCreated(widget: DashboardWidget): void {
            widget.x_pos = widget.x_pos ?? 0;
            widget.y_pos = widget.y_pos ?? 0;
            widget.width = widget.width ?? 3;
            widget.height = widget.height ?? 3;
            widget.hide = false;

            this.dashboard.widgets.push(widget);

            this.$timeout((): void => {
                if (!this.grid) return;
                const root: HTMLElement = this.$element[0];
                const el = root.querySelector(
                    `#grid-stack-container [gs-id="${widget.id}"]`
                ) as GridItemHTMLElement;
                if (el) {
                    this.grid.makeWidget(el);
                }
            });
        }

        broadcastResize() {
            this.$scope.$broadcast("resized");
        }

        widgetDeleted(widget: DashboardWidget): void {
            const items: GridItemHTMLElement[] = this.grid.getGridItems();
            angular.forEach(items, (item: GridItemHTMLElement): void => {
                if (item.gridstackNode.id == widget.id) {
                    this.grid.removeWidget(item);
                }
            });
            const idx = this.dashboard.widgets.findIndex(
                (w: DashboardWidget): boolean => w.id == widget.id
            );
            if (idx > -1) {
                this.dashboard.widgets.splice(idx, 1);
            }
        }

        saveDashboard() {
            const grid: any = angular.copy(this.dashboard);
            grid.widgets = this.grid.save(false, false);
            this.dataProvider.saveGrid(grid);
        }

        createWidgetDialog(ev: any) {
            this.dialog
                .show({
                    component: "vc-create-widget",
                    targetEvent: ev,
                    scope: this.$scope,
                    clickOutsideToClose: false,
                    ariaLabel: "Duplicate Report",
                    size: "xx-large",
                    fullscreen: true,
                    forceFullscreen: true,
                    locals: {
                        dashboard: this.dashboard,
                        globalFilter: this.filter,
                    },
                })
                .then((widget: any) => {
                    this.widgetCreated(widget);
                });
        }
    },
};

angular
    .module("dashboard")
    .component(DashboardComponent.selector, DashboardComponent);
